import React, { memo, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { EmptyTableMessage } from 'src/components/payments-ops/EmptyTableMessage'
import { GridTable } from 'src/components/shared/grid/GridTable'
import { PAYMENTS_OPS_PAGE_SIZE_OPTIONS } from 'src/constants/payments-ops/constants'
import { buildPendingPaymentColumns } from 'src/constants/payments-ops/pendingPaymentsGrid'
import type { PendingPayment } from 'src/interfaces/paymentsReview'
import { getPaymentReviewSubmitting, getPendingPaymentsLoading } from 'src/selectors/paymentsReview'
import { useGridPagination } from 'src/utils/hooks/grid/useGridPagination'

interface Props {
  rows: PendingPayment[]
  onReview(payment: PendingPayment): void
}

const getRowId = (row: PendingPayment): string => {
  return row.id
}

const renderEmpty = () => {
  return <EmptyTableMessage message="No pending payments to review." />
}

const PendingPaymentsTableComponent = ({ rows, onReview }: Props) => {
  const isLoading = useSelector(getPendingPaymentsLoading)
  const isSubmitting = useSelector(getPaymentReviewSubmitting)
  const { paginationModel, setPaginationModel } = useGridPagination(PAYMENTS_OPS_PAGE_SIZE_OPTIONS)

  const handleReview = useCallback(
    (payment: PendingPayment) => {
      onReview(payment)
    },
    [onReview],
  )

  const columns = useMemo(() => {
    return buildPendingPaymentColumns(handleReview, isSubmitting)
  }, [handleReview, isSubmitting])

  return (
    <GridTable<PendingPayment>
      rows={rows}
      columns={columns}
      getRowId={getRowId}
      isLoading={isLoading}
      fillHeight
      sortingMode="client"
      paginationMode="client"
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
      pageSizeOptions={PAYMENTS_OPS_PAGE_SIZE_OPTIONS}
      EmptyElement={renderEmpty}
      tableDataTestId="payments-review-table"
      rowDataTestIdPrefix="payments-review"
    />
  )
}

export const PendingPaymentsTable = memo(PendingPaymentsTableComponent)
